import { FC } from "react";
import { CalendarDaysIcon, ClockIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";

interface ModernPendingReservationsHeaderProps {
  totalCount: number;
  onRefresh?: () => void;
}

const ModernPendingReservationsHeader: FC<ModernPendingReservationsHeaderProps> = ({ totalCount, onRefresh }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="mb-8"
    >
      <div className="bg-gradient-to-r from-white via-orange-50/30 to-amber-50/30 dark:from-gray-800 dark:via-orange-900/20 dark:to-amber-900/20 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg backdrop-blur-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Title Section */}
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-lg">
              <CalendarDaysIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Demandes de réservation</h1>
              <div className="flex items-center space-x-2 mt-1">
                <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <span className="text-lg text-gray-600 dark:text-gray-300">
                  {totalCount} demande{totalCount !== 1 ? "s" : ""} en attente
                </span>
                {totalCount > 0 && (
                  <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-sm font-medium rounded-full">
                    Action requise
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            {onRefresh && totalCount > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onRefresh}
                className="flex items-center space-x-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 shadow-sm"
              >
                <svg
                  className="w-5 h-5 text-gray-600 dark:text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span className="text-gray-700 dark:text-gray-300 font-medium">Actualiser</span>
              </motion.button>
            )}
          </div>
        </div>

        {/* Statistics Bar */}
        {totalCount > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-6 pt-6 border-t border-gray-200/50 dark:border-gray-700/50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-orange-50/50 dark:bg-orange-900/20 rounded-xl">
                <div className="flex items-center justify-center mb-2">
                  <ClockIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{totalCount}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">En attente</div>
              </div>
              <div className="text-center p-4 bg-blue-50/50 dark:bg-blue-900/20 rounded-xl">
                <div className="flex items-center justify-center mb-2">
                  <CalendarDaysIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalCount}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total demandes</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {totalCount === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-center py-8"
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <CalendarDaysIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Aucune demande en attente</h3>
            <p className="text-gray-500 dark:text-gray-400">Toutes vos demandes de réservation ont été traitées</p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default ModernPendingReservationsHeader;
